/* eslint-disable max-lines */
import { Component } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Subscription, combineLatest, zip, iif, of } from 'rxjs';
import { map, share, switchMap, take, tap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import omitBy from 'lodash-es/omitBy';

import { isNullOrUndefined } from '@app/util/util';
import { HotelSearched } from '@hotels/hotel-results/entities/hotel-searched';
import { HotelResultsFacade } from '@hotels/hotel-results/hotel-results.facade';
import {
  PickedRange, FilterSorter, FilterSorterCriterias, CheckboxFilter
} from '@shared/filters/entities/fetch-filters.response';
import { HotelMarker } from '@hotels/hotel-results/entities/hotel-marker';
import {
  pickedCheckboxes, pickedRanges, pickedStars, pickedSorter
} from '@hotels/hotel-results/mappers/hotel-filters.mapper';
import { HotelSearchPayload } from '@hotels/hotel-results/entities/hotel-search.payload';
import {
  HotelFilterApplier, HotelFilterProvider, HotelFilterTypes
} from '@shared/hotel-itinerary/hotel-filters';
import { HotelsFiltersComponent } from '@hotels/hotel-results/components/hotels-filters/hotels-filters.component';
import { HOTEL_BYHOURS_RESULTS_ROUTE, HOTEL_RESULTS_ROUTE } from '@hotels/hotel-results/constants/routes';
import { HotelFilterSearchPayload } from '@hotels/hotel-results/entities/hotel-filter-search.payload';
import { totalNights } from '@ux/mappers/guest.mapper';
import { AMPLITUDE_EVENTS, EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';
import { AppFacade } from '@app/app.facade';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { HotelResultsBaseAbstractComponent } from '@hotels/hotel-results/abstract/hotel-results-base.abstract';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { triggerAmplitudeEventApp } from '@amplitude/helpers/gtm-functions';
import { HotelDestinationInputType } from '@shared/base-search-bar/entities/autocomplete-destionation';
import { PlaceNamePipe } from '@app/pipes/place-name.pipe';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { ErrorHandle } from '@shared/no-results/entities/results-errors';
import { AvoService } from '@avo/services/avo.service';
import { HotelFilterVm, HotelFiltersResponse } from '@shared/magic-filter-bar/entities/filter-types';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { BYHOURS_BANNER_AVAILABLE, BannerResultsType } from '@ux/mappers/banner-results.mapper';
import { NavigationService } from '@app/services/navigation.service';

@Component({
  selector: 'app-search-bar-abstract',
  template: '',
  standalone: false
})
export class HotelResultsAbstractComponent extends HotelResultsBaseAbstractComponent {

  protected params: HotelSearchPayload = {} as HotelSearchPayload;
  protected maxHotelsForMap: number = 1000;
  protected componentSubscriptions: Subscription[] = [];
  protected hotelResultsRootRoute: string = HOTEL_RESULTS_ROUTE;
  protected byhoursResultsRootRoute: string = HOTEL_BYHOURS_RESULTS_ROUTE;

  constructor(
    protected facade: HotelResultsFacade,
    protected appFacade: AppFacade,
    protected route: ActivatedRoute,
    protected router: Router,
    protected viewportScroller: ViewportScroller,
    protected mobileDeviceService: MobileDeviceService,
    protected amplitude: PropertiesAmplitudeService,
    protected domManipulateService: DomManipulateService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected placeNamePipe: PlaceNamePipe,
    protected avoService: AvoService,
    protected baseSearchBarFacade: BaseSearchBarFacade,
    protected navigationService: NavigationService,
  ) {
    super(route, navigationService);
  }
  get isPrime$(): Observable<boolean> {
    return this.appFacade.isPrime$;
  }
  get hasRappiCard$(): Observable<boolean> {
    return this.appFacade.hasRappiCard$;
  }
  get isLoggedIn$(): Observable<boolean> {
    return this.appFacade.isLoggedIn$;
  }
  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get hourType$(): Observable<string[]> {
    return this.baseSearchBarFacade.hourType$;
  }

  get hotelResults$(): Observable<HotelSearched[]> {
    return combineLatest([
      this.facade.hotelResults$,
      this.baseSearchBarFacade.hourType$
    ]).pipe(
      map(([hotels, typeHours]) => {
        return this.isByHours ? this.sortHotelsByPackHours(hotels, typeHours) : hotels;
      })
    );
  }
  get isLoadingHotelResults$(): Observable<boolean> {
    return this.facade.isLoadingHotelResults$;
  }
  get hotelSearchResultCount$(): Observable<number> {
    return this.facade.hotelSearchResultCount$;
  }
  get hotelSearchResultTotalCount$(): Observable<number> {
    return this.facade.hotelSearchResultTotalCount$;
  }
  get isLoadingHotelFilters$(): Observable<boolean> {
    return this.facade.isLoadingHotelFilters$;
  }
  get hotelFilters$(): Observable<HotelFilterVm[]> {
    return this.facade.hotelFilters$;
  }
  get hotelFiltersResponse$(): Observable<HotelFiltersResponse> {
    return this.facade.hotelFiltersResponse$;
  }
  get hotelsWithMarkers$(): Observable<HotelMarker[]> {
    return this.facade.hotelsWithMarkers$;
  }
  get params$(): Observable<Params> {
    return this.route.params;
  }
  get errors$(): Observable<ErrorHandle> {
    return this.facade.errors$;
  }
  get errorInHotels$(): Observable<boolean> {
    return combineLatest(
      [this.facade.errorInHotels$, this.emptyResults$]
    ).pipe(map(([errorInHotels, emptyResults]) => errorInHotels || emptyResults
    ));
  }
  get rooms(): string {
    return this.route.snapshot.params['rooms'];
  }
  get exclusiveOfferFilter$(): Observable<boolean> {
    return this.facade.hotelFiltersPayload$.pipe(map(payload => {
      return payload.MemberRatesOnly;
    }
    ), share());
  }
  get pickedCheckboxes$(): Observable<string[]> {
    return this.facade.hotelFiltersPayload$.pipe(
      map(payload => pickedCheckboxes(payload)), share());
  }

  get pickedTotalDiscountPercentage$(): Observable<number> {
    return this.facade.hotelFiltersPayload$.pipe(map(payload => payload.TotalDiscountPercentageFrom), share());
  }

  get pickedStars$(): Observable<string[]> {
    return this.facade.hotelFiltersPayload$.pipe(map(payload => pickedStars(payload)), share());
  }

  get hotelNameSearch$(): Observable<string> {
    return this.facade.hotelFiltersPayload$.pipe(map(payload => payload.HotelName), share());
  }

  get pickedRanges$(): Observable<PickedRange> {
    return this.facade.hotelFiltersPayload$.pipe(map(payload => pickedRanges(payload)), share());
  }

  get pickedSorter$(): Observable<FilterSorterCriterias> {
    return this.facade.hotelFiltersPayload$.pipe(map(payload => pickedSorter(payload)), share());
  }

  get hasResults$(): Observable<boolean> {
    return this.hotelResults$.pipe(map((results: HotelSearched[]) => !isNullOrUndefined(results)));
  }

  get searchedHotel$(): Observable<HotelSearched> {
    return this.facade.searchedHotel$;
  }

  get loadingResults$(): Observable<boolean> {
    return combineLatest([this.isLoadingHotelResults$, this.hasResults$])
      .pipe(map(([isLoadingHotelResults, hasResults]) => !!isLoadingHotelResults || !hasResults));
  }

  get emptyResults$(): Observable<boolean> {
    return this.hotelResults$.pipe(map((results: HotelSearched[]) => results?.length === 0));
  }

  get hasFilters$(): Observable<boolean> {
    return combineLatest([this.pickedCheckboxes$, this.pickedStars$, this.pickedRanges$,
      this.hotelNameSearch$, this.exclusiveOfferFilter$, this.pickedTotalDiscountPercentage$]).pipe(
      map(([checkboxes, stars, ranges, name, exclusiveOf, totalDiscountPercentage]) =>
        !isEmpty(checkboxes) || !isEmpty(stars) ||
        !isEmpty(ranges) || !isEmpty(name) ||
        exclusiveOf || (!!totalDiscountPercentage && totalDiscountPercentage > 1)));
  }

  get searchPayload$(): Observable<HotelFilterSearchPayload> {
    return this.facade.hotelFiltersPayload$;
  }

  get checkIn$(): Observable<string> {
    return this.route.paramMap.pipe(map(params => params.get('check_in') ?
      params.get('check_in').replace(new RegExp(/-/g), '/') : params.get('check_in')));
  }

  get checkOut$(): Observable<string> {
    return this.route.paramMap.pipe(map(params => params.get('check_out') ?
      params.get('check_out').replace(new RegExp(/-/g), '/') : params.get('check_out')));
  }

  get totalNightsNumber$(): Observable<number> {
    return combineLatest([this.checkIn$, this.checkOut$]).pipe(map(([checkIn, checkOut]) => totalNights(checkOut, checkIn)));
  }

  get placeTitleSelection$(): Observable<string> {
    if (this.inputDestinationPlace?.InputType) {
      switch (this.inputDestinationPlace.InputType) {
        case HotelDestinationInputType.hotel:
          return this.placeNamePipe.transform(this.inputDestinationPlace.NearestIata, this.headerListCriteria);
        default:
          const split = this.inputDestinationPlace?.Name?.split(',');
          const value = split && split.length ? split[0] : null;
          return of(value);
      }
    } else return of(null);
  }

  get hasMoreThanOneResult$(): Observable<boolean> {
    return this.hotelResults$.pipe(map(results => results?.length > 1));
  }

  get showMobileFilters$(): Observable<boolean> {
    return combineLatest([this.loadingResults$, this.errorInHotels$]).pipe(
      map(([loadingResults, errorInHotels]) => !loadingResults && !errorInHotels));
  }

  get hotelTypeBanner(): BannerResultsType {
    return this.isByHours ? BYHOURS_BANNER_AVAILABLE: this.hotelsBanner;
  }

  protected get isMapView(): boolean {
    return this.filtersProvider === HotelFilterProvider.HotelMap;
  }

  public showResultItem$(hotelItem: HotelSearched): Observable<boolean> {
    return this.searchedHotel$.pipe(map(searchedHotel => !!isNullOrUndefined(searchedHotel) || (
      searchedHotel?.HotelCode !== hotelItem.HotelCode && searchedHotel?.HotelId !== hotelItem.HotelId
    )));
  }

  public setFiltersUp(state: boolean): void {
    this.isFilterUp = state;
  }

  public toggleShowResultsMap(): void {
    this.showResultsMap = !this.showResultsMap;
    this.registerViewTypeEvent();
  }

  public registerViewTypeEvent(): void {
    const viewType = this.showResultsMap ? EVENT_SOURCE.MAP : EVENT_SOURCE.LIST;
    this.facade.hotelFiltersPayload$.pipe(
      take(1),
      switchMap(payload => this.amplitude.searchProperties$(SearchBy.HOTELS)
        .pipe(
          take(1),
          switchMap(searchProps => this.triggerAmplitudeService.trackAmplitudeEventProps$(
            AMPLITUDE_EVENTS.SELECT_VIEW_TYPE, { VIEW_TYPE: viewType }, searchProps
          ).pipe(
            tap(trigger => {
              if (trigger) triggerAmplitudeEventApp(trigger);
              if (this.showResultsMap) this.facade.searchHotelsMarkers(payload);
            })
          ))
        ))
    ).subscribe().unsubscribe();
  }

  public scrollResults(): void {
    this.facade.disabledHotelResultsScroll$.pipe(take(1)).subscribe(disabled => {
      if (!disabled && 
          (this.router.url.includes(this.hotelResultsRootRoute) || this.router.url.includes(this.byhoursResultsRootRoute))) {
        this.facade.searchHotelsWithPagination();
      }
    }).unsubscribe();
  }

  public resetHotelFilters(): void {
    this.facade.resetHotelFilters(this.isMapView);
    this.facade.hotelSortingHasBeenChanged(false);
  }

  public applySorter(sorter: FilterSorter, isMobile?: boolean): void {
    this.facade.setFilterSorter(sorter);
    if (isMobile && this.isMapView) {
      this.facade.fetchHotelsMarkersWithFilters();
    } else {
      this.facade.fetchHotelsWithFilters();
    }
  }

  // eslint-disable-next-line @typescript-eslint/typedef
  public applyFilter(_filter: HotelFilterApplier, isMobile = false): void {
    if ((!isNullOrUndefined(_filter.removable) && !_filter.removable) ||
    (isNullOrUndefined(_filter.removable) && !isEmpty(_filter.value))) {
      zip(
        this.amplitude.searchProperties$(SearchBy.HOTELS),
        iif(
          () => _filter.type === HotelFilterTypes.Ammenities,
          this.facade.hotelFilters$.pipe(
            map(filters => (filters?.find(filter => _filter.type === filter.type)?.value as
              CheckboxFilter[])?.find(fil => fil.Text === _filter.value)?.TextTransformed)
          ),
          of(null)
        )
      ).pipe(
        map(([searchProps, selectedComfort]) => ({
          searchProps,
          eventParams: omitBy({
            FILTERGROUP: _filter.type,
            FILTERVALUE: selectedComfort ? selectedComfort : _filter.value,
          }, isNullOrUndefined)
        })),
        switchMap(({ searchProps, eventParams }) => this.triggerAmplitudeService.trackAmplitudeEventProps$(
          AMPLITUDE_EVENTS.SELECT_FILTER, searchProps, eventParams
        ).pipe(
          tap(trigger => triggerAmplitudeEventApp(trigger)),
        ))
      ).subscribe().unsubscribe();
    }

    this.viewportScroller.scrollToPosition([0, 0]);
    if (_filter.type === HotelFilterTypes.PackHours) {
      this.baseSearchBarFacade.selectHourType(_filter.value as string[]);
    }
    if (isMobile && this.isMapView) {
      this.facade.fetchHotelsMarkersWithFilters(_filter);
    } else this.facade.fetchHotelsWithFilters(_filter);
  }

  public viewMembersRateOffer(filter: HotelFilterApplier) {
    this.facade.setFilterSorter({OrderCriteria: SorterTypes.DiscountPercentage, OrderDesc: true});
    this.applyFilter(filter)
  }

  public onActivate(componentReference: HotelsFiltersComponent): void {
    if (componentReference instanceof HotelsFiltersComponent) {
      this.componentSubscriptions.push(
        combineLatest(
          [this.hotelFilters$, this.isLoadingHotelFilters$, this.isMobile$, this.hotelFiltersResponse$, this.hourType$]
        ).subscribe(([filters, isLoading, isMobile, hotelFiltersResponse, hourType]) => {
          componentReference.filters = filters;
          componentReference.isLoading = isLoading;
          componentReference.isMobile = isMobile;
          componentReference.sorters = hotelFiltersResponse?.Sorters;
          componentReference.hourType = hourType;
          componentReference.isByHours = this.isByHours;
        })
      );
      this.componentSubscriptions.push(
        combineLatest([componentReference.sorter.pipe(map(data => data as FilterSorter)), this.isMobile$])
          .subscribe(([data, isMobile]) => {
            if (isMobile) {
              this.applySorter(data, componentReference.isMobile);
              this.facade.hotelSortingHasBeenChanged(true);
            }
          })
      );
      this.componentSubscriptions.push(
        combineLatest([componentReference.filter.pipe(map(data => data as HotelFilterApplier)), this.isMobile$])
          .subscribe(([data, isMobile]) => {
            if (isMobile) this.applyFilter(data, componentReference.isMobile);
          })
      );
      this.componentSubscriptions.push(
        this.pickedCheckboxes$.subscribe(data => componentReference.pickedCheckboxes = data)
      );
      this.componentSubscriptions.push(
        this.pickedTotalDiscountPercentage$.subscribe(data => componentReference.pickedTotalDiscountPercentage = data)
      );
      this.componentSubscriptions.push(
        this.pickedRanges$.subscribe(data => componentReference.pickedRanges = data)
      );
      this.componentSubscriptions.push(
        this.pickedStars$.subscribe(data => componentReference.pickedStars = data)
      );
      this.componentSubscriptions.push(
        this.pickedSorter$.subscribe(data => componentReference.pickedSorter = data)
      );
      this.componentSubscriptions.push(
        this.hotelNameSearch$.subscribe(data => componentReference.hotelNameSearch = data)
      );
      this.componentSubscriptions.push(
        this.totalNightsNumber$.subscribe(data => componentReference.hotelNights = data)
      );
      this.componentSubscriptions.push(
        this.exclusiveOfferFilter$.subscribe(data => componentReference.exclusiveOffer = data)
      );
      this.componentSubscriptions.push(
        this.facade.sortingHasBeenChanged$.subscribe(data => componentReference.hasChangeSortByFilter = data)
      );
      this.componentSubscriptions.push(
        componentReference.clear.subscribe(() => {
          this.resetHotelFilters();
        })
      );
    }
  }

  public onDeactivate(componentReference: Component): void {
    if (componentReference.constructor.name === HotelsFiltersComponent.name.toString() && this.showMapResultFilters) {
      this.showResultsMap = true;
    }
    this.componentSubscriptions.filter((s) => !!s).forEach((s: Subscription) => s.unsubscribe());
  }

  public sortHotelsByPackHours(hotelData: HotelSearched[], desiredHours: string[]): HotelSearched[] {
    const maxHourDesired = Math.max(...(desiredHours.length ? desiredHours.map(hour => +hour) : []));
    const sortedHotelData = hotelData.map(hotel => {
      const sortedRoomRates = [...hotel.RoomRates].sort((a, b) => {
        const aIsDesired = a.PackHours === maxHourDesired ? 1 : 0;
        const bIsDesired = b.PackHours === maxHourDesired ? 1 : 0;
        return bIsDesired - aIsDesired;
      });
      return {...hotel, RoomRates: sortedRoomRates};
    });
    sortedHotelData.sort((a, b) => {
      const aHasDesiredHours = a.RoomRates.some(rate => rate.PackHours === maxHourDesired) ? 1 : 0;
      const bHasDesiredHours = b.RoomRates.some(rate => rate.PackHours === maxHourDesired) ? 1 : 0;
      return bHasDesiredHours - aHasDesiredHours;
    });
    return sortedHotelData;
  }

}
